import { 
  Box, 
  LinearProgress, 
  styled, 
  Typography, 
  Chip,
  Stack
} from "@mui/material";
import { CheckCircle, Schedule, TrendingUp } from "@mui/icons-material";

const ProgressContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  margin: theme.spacing(2, 0),
}));

const StyledLinearProgress = styled(LinearProgress)(({ theme, isComplete }) => ({
  height: 12,
  borderRadius: 6,
  backgroundColor: theme.palette.grey[200],
  "& .MuiLinearProgress-bar": {
    borderRadius: 6,
    transition: "all 0.4s ease-in-out",
    backgroundColor: isComplete 
      ? theme.palette.success.main 
      : theme.palette.primary.main,
    background: isComplete 
      ? `linear-gradient(45deg, ${theme.palette.success.main} 30%, ${theme.palette.success.light} 90%)`
      : `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
  },
}));

const ChecklistProgress = ({ items }) => {
  const total = items.length;
  const completed = items.filter((item) => item.isChecked).length;
  const pending = total - completed;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
  
  const isComplete = percent === 100;
  const isEmpty = total === 0;

  if (isEmpty) {
    return (
      <ProgressContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Nenhum item no checklist
          </Typography>
        </Box>
      </ProgressContainer>
    );
  }

  return (
    <ProgressContainer>
      {/* Header com título e status */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingUp color={isComplete ? "success" : "primary"} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Progresso
          </Typography>
        </Box>
        
        <Chip
          icon={isComplete ? <CheckCircle /> : <Schedule />}
          label={isComplete ? "Concluído" : "Em Andamento"}
          color={isComplete ? "success" : "primary"}
          variant={isComplete ? "filled" : "outlined"}
          size="small"
        />
      </Box>

      {/* Barra de progresso */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {completed} de {total} itens completos
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 700,
              color: isComplete ? 'success.main' : 'primary.main'
            }}
          >
            {percent}%
          </Typography>
        </Box>
        
        <StyledLinearProgress 
          variant="determinate" 
          value={percent} 
          isComplete={isComplete}
        />
      </Box>

      {/* Estatísticas */}
      <Stack direction="row" spacing={2} justifyContent="center">
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="success.main" sx={{ fontWeight: 600 }}>
            {completed}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Completos
          </Typography>
        </Box>
        
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="warning.main" sx={{ fontWeight: 600 }}>
            {pending}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Pendentes
          </Typography>
        </Box>
        
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="text.primary" sx={{ fontWeight: 600 }}>
            {total}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Total
          </Typography>
        </Box>
      </Stack>
    </ProgressContainer>
  );
};

export default ChecklistProgress;
